/**
 * Per-event management goal: goal statement + KPI thresholds + alert config +
 * the persisted latest AI cohort analysis. Mirrors the backend contract in
 * `org.nikita.app.event_goal` — backend is the source of truth; the enums
 * below MUST stay byte-identical to `KpiMetric` / `ThresholdDirection`.
 */

export type KpiMetric =
  | "GUESTS_TOTAL"
  | "GUESTS_ACCEPTED"
  | "GUESTS_DECLINED"
  | "GUESTS_PENDING"
  | "ACCEPTANCE_RATE"
  | "CHECKIN_COUNT"
  | "CHECKIN_RATE"
  | "EMAIL_DELIVERY_RATE"
  | "APPOINTMENTS_BOOKED"
  | "SEATING_OCCUPANCY_RATE"
  | "EMAIL_OPEN_RATE"
  | "EMAIL_CLICK_RATE"
  | "EMAIL_BOUNCE_RATE"
  | "APPOINTMENTS_CONFIRMED"
  | "COHORT_SHARE";

export const KPI_METRICS: KpiMetric[] = [
  "GUESTS_TOTAL",
  "GUESTS_ACCEPTED",
  "GUESTS_DECLINED",
  "GUESTS_PENDING",
  "ACCEPTANCE_RATE",
  "CHECKIN_COUNT",
  "CHECKIN_RATE",
  "EMAIL_DELIVERY_RATE",
  "APPOINTMENTS_BOOKED",
  "SEATING_OCCUPANCY_RATE",
  "EMAIL_OPEN_RATE",
  "EMAIL_CLICK_RATE",
  "EMAIL_BOUNCE_RATE",
  "APPOINTMENTS_CONFIRMED",
  // Last on purpose: the one metric that needs two more fields
  // (dimension + bucketValue) — pickers list it after the plain ones.
  "COHORT_SHARE",
];

/** Metrics whose values (and thresholds/targets) are percentages 0–100. */
export const RATE_METRICS: ReadonlySet<KpiMetric> = new Set<KpiMetric>([
  "ACCEPTANCE_RATE",
  "CHECKIN_RATE",
  "EMAIL_DELIVERY_RATE",
  "SEATING_OCCUPANCY_RATE",
  "EMAIL_OPEN_RATE",
  "EMAIL_CLICK_RATE",
  "EMAIL_BOUNCE_RATE",
  "COHORT_SHARE",
]);

/**
 * Metrics that should stay LOW. Their alert fires when the value climbs ABOVE
 * the threshold, so the editor defaults their direction to ABOVE and the
 * forecast never extrapolates them toward a "target to reach".
 */
export const KEEP_LOW_METRICS: ReadonlySet<KpiMetric> = new Set<KpiMetric>([
  "GUESTS_DECLINED",
  "GUESTS_PENDING",
  "EMAIL_BOUNCE_RATE",
]);

/**
 * A metric label that survives a BACKEND AHEAD of this bundle: an enum value
 * this build does not know yet must never render as the raw i18n key
 * ("goals.kpis.metric.FOO"). Known metrics go through the translator.
 */
export function metricLabel(
  metric: string,
  t: (key: string) => string,
): string {
  return (KPI_METRICS as string[]).includes(metric)
    ? t(`kpis.metric.${metric}`)
    : metric.replaceAll("_", " ").toLowerCase();
}

/**
 * What a KPI is CALLED wherever one is picked or listed: two cohort KPIs are
 * both "Cohort share" by metric — their cohort is the name.
 */
export function kpiTitle(
  kpi: {
    metric: string;
    bucketValue?: string | null;
    dimension?: string | null;
    baseDimension?: string | null;
    baseValue?: string | null;
  },
  t: (key: string, values?: Record<string, string>) => string,
  /** Display name for the stored bucket value (`useCohortValueLabel`) — a
   *  built-in enum code reads as its catalogue name, not as "UNCONTACTED".
   *  The KPI's own facet rides along: a CUSTOM field's options are the
   *  organiser's words and are never translated. Omitted = the raw value,
   *  which is what every caller showed before. */
  valueLabel: (raw: string, dimension?: string | null) => string = (raw) => raw,
): string {
  if (kpi.metric === "COHORT_SHARE" && kpi.bucketValue && kpi.baseValue) {
    // Within a group (W3): "Probefahrt gebucht – unter Neukunden".
    return t("kpis.withinGroupTitle", {
      bucket: valueLabel(kpi.bucketValue, kpi.dimension),
      group: valueLabel(kpi.baseValue, kpi.baseDimension),
    });
  }
  return kpi.metric === "COHORT_SHARE" && kpi.bucketValue
    ? t("kpis.cohortShareTitle", { bucket: valueLabel(kpi.bucketValue, kpi.dimension) })
    : metricLabel(kpi.metric, t);
}

export type ThresholdDirection = "BELOW" | "ABOVE";

export type AlertState = "ARMED" | "ALERTED";

export interface EventGoalKpi {
  /** Server-minted hex id. Absent on not-yet-saved rows. */
  id?: string;
  metric: KpiMetric;
  target: number;
  thresholdValue: number;
  thresholdDirection: ThresholdDirection;
  /**
   * ISO instant (UTC midnight of the picked day). NOT display-only: once it
   * passes with the target unmet the backend mails a one-shot "deadline missed"
   * alert, and it is the forecast's due date (the event start stands in when
   * it is absent — see {@link KpiValue.effectiveDeadline}).
   */
  deadline?: string;
  enabled?: boolean;
  /** COHORT_SHARE only: the guest facet (built-in field or `additionalFields.<name>`). */
  dimension?: string;
  /** COHORT_SHARE only: the EXACT stored value that makes a guest a member. */
  bucketValue?: string;
  /**
   * COHORT_SHARE only: an AFTER-EVENT success measure — the share of guests
   * whose yes/no field is filled in once the event is over ("test drive
   * booked"). The backend sets its due date (event end + 14 days) and keeps
   * it out of alerts and the score until then (goals "abstract" wave W2).
   */
  outcome?: boolean;
  /**
   * COHORT_SHARE only, both or neither: the GROUP the share is taken within —
   * "test drive booked among the prospects" counts `bucketValue` among the
   * guests whose `baseDimension` is `baseValue` (goals "abstract" wave W3).
   * Absent = the whole guest list.
   */
  baseDimension?: string;
  baseValue?: string;
  // ---- server-managed (read-only on the client) ----
  lastAlertState?: AlertState;
  lastAlertAt?: string;
  lastEvaluatedValue?: number;
  lastEvaluatedAt?: string;
}

/** @deprecated legacy single-choice recipient mode. The backend maps it to the
 *  additive fields below on read; new writes never set it. */
export type AlertRecipientMode = "ADMINS" | "CUSTOM";

export type DigestInterval = "WEEKLY" | "BIWEEKLY" | "MONTHLY";
export const DIGEST_INTERVALS: DigestInterval[] = ["WEEKLY", "BIWEEKLY", "MONTHLY"];

export type Weekday =
  | "MONDAY"
  | "TUESDAY"
  | "WEDNESDAY"
  | "THURSDAY"
  | "FRIDAY"
  | "SATURDAY"
  | "SUNDAY";
export const WEEKDAYS: Weekday[] = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
];

/**
 * Email-alert settings. Recipients are ADDITIVE: the final list is the union
 * of (all admins when `notifyAdmins`) + (`adminRecipientIds` collaborators) +
 * `customEmails`. Byte-for-byte mirror of backend `AlertConfig`.
 */
export interface GoalAlertConfig {
  /** @deprecated legacy; retained only so old drafts round-trip. */
  recipientMode?: AlertRecipientMode;
  /** Notify every valid event owner/admin. */
  notifyAdmins: boolean;
  /** Specific event-role ids (collaborators/admins) to notify. */
  adminRecipientIds: string[];
  /** Literal recipient emails, combinable with the admin fields. */
  customEmails: string[];
  enabled: boolean;
  /** Master switch for the KPI-overview digest email. */
  weeklyDigest?: boolean;
  /** Digest cadence when `weeklyDigest` is on. */
  digestInterval?: DigestInterval;
  /** Weekday the digest is sent on. */
  digestWeekday?: Weekday;
  /** Server-managed phase anchor for BIWEEKLY. */
  digestAnchor?: string;
}

/**
 * Measurable cohort goal: "targetPercent % of the roster belongs to cohort
 * `label`" (e.g. 40% C-Level). `dimension` optionally names the guest facet
 * ("jobTitle", "origin", "additionalFields.<name>") so the analysis can ground
 * the actual share deterministically. Mirror of backend `CohortTarget`.
 */
export interface CohortTarget {
  dimension?: string;
  label: string;
  targetPercent: number;
  /**
   * "AT_MOST" = a cap: met at or below `targetPercent`, missed above it (too
   * many existing customers). Absent = at least — what every target was
   * before (direction 2026-10-05). See `lib/goals/targetDirection`.
   */
  direction?: "AT_MOST";
}

export type GoalAnalysisStatus = "NONE" | "RUNNING" | "COMPLETED" | "FAILED";

export interface GoalCohortBucket {
  label: string;
  count: number;
  /** 0–1 share of the analyzed guest set. */
  share?: number;
  /**
   * false = COUNTED in Java (the label matched a stored value exactly).
   * true = the label matched no stored value; the count is the model's guess.
   * Absent on analyses persisted before the field existed — read absent as
   * estimated.
   */
  estimated?: boolean;
}

export interface GoalCohort {
  /** Dimension key, e.g. "origin", "country", "additionalFields.<name>". */
  dimension: string;
  label: string;
  buckets: GoalCohortBucket[];
  finding: string;
}

export interface GapGuestFilter {
  key: string;
  operator: string;
  value: string;
}

export interface GoalGap {
  title: string;
  reason: string;
  suggestedAction: string;
  /** Optional guest-list handoff filter (exact guestlist filter row shape). */
  guestFilters?: GapGuestFilter[];
}

/** Target-vs-actual for one CohortTarget, produced by the analysis reduce pass. */
export interface GoalTargetResult {
  dimension?: string;
  label: string;
  targetPercent: number;
  /** Mirrors the target's direction when the run saw one: "AT_MOST" = a cap. */
  direction?: "AT_MOST";
  /**
   * The share in percent. Absent (null) only on an `excludedFromAnalysis`
   * row without a literal hit — no count and no estimate exists for it
   * (audit r3 MED-2); read such a row with `lib/goals/targetGrade`.
   */
  actualPercent?: number | null;
  /**
   * false = COUNTED by the backend over the full roster (the target names a
   * facet). true = the model estimated it. Absent on analyses persisted before
   * the field existed — read absent as estimated.
   */
  estimated?: boolean;
  /** n — guests counted as members (counted rows only; absent when estimated). */
  matched?: number | null;
  /** N — all active guests incl. companions and declines (the share's denominator). */
  total?: number | null;
  /**
   * true = a target on a field the model may not see (SENSITIVE / ART9,
   * IMAGE, EMAIL, or no current definition — renamed or deleted): kept out of
   * the AI analysis — the prompt, the score, the verdict and the
   * recommendations never saw it (ship 2026-10-06 audit r2 MED-2). With a
   * literal hit it is counted in Java for the organiser (`estimated: false`,
   * `matched`, `actualPercent`) and graded on its row; without one it carries
   * no number (`estimated: true`) and reads "not counted" (audit r3 MED-2).
   * Never read to Velo.
   */
  excludedFromAnalysis?: boolean;
}

/**
 * How much of the roster the run actually saw: the map pass chunks the
 * guest list and a chunk the model dropped is missing from every number
 * below. `singlePass` = the roster fit one call (no reduce).
 */
export interface GoalAnalysisCoverage {
  guests?: number;
  chunks?: number;
  droppedChunks?: number;
  singlePass?: boolean;
  /** W3a: the roster's raw rows went into the one reduce call (small rosters only). */
  rosterInlined?: boolean;
  /** W3a: the chunked map pass ran (flag-gated server-side, default off). */
  mapPass?: boolean;
}

/**
 * W3a: how the score was made (server-side `GoalScore`). `computed` = from the
 * counted target rows and the evaluated KPIs (attainments 0..1, weights sum
 * to 1); `model` = nothing was countable so the model's own integer stands.
 * Absent on docs analyzed before the wave.
 */
export interface GoalAnalysisScoreBreakdown {
  /** "none" = nothing to grade (empty roster): score is absent, render no line. */
  source: "computed" | "model" | "none";
  targetAttainment?: number;
  kpiAttainment?: number;
  targetWeight?: number;
  kpiWeight?: number;
  countedTargets?: number;
  estimatedTargets?: number;
  evaluatedKpis?: number;
  modelScore?: number;
}

export interface GoalAnalysis {
  status: GoalAnalysisStatus;
  /** 0–100 goal-fit score. */
  score?: number;
  /** W3a: the score's receipt — absent on older docs (then only the score shows). */
  scoreBreakdown?: GoalAnalysisScoreBreakdown;
  verdictText?: string;
  cohorts?: GoalCohort[];
  gaps?: GoalGap[];
  targetResults?: GoalTargetResult[];
  analyzedAt?: string;
  startedAt?: string;
  /** BackgroundJob id of the last manual run. */
  jobId?: string;
  error?: string;
  /** Server-managed run receipt (2026-09-21) — absent on older docs. */
  coverage?: GoalAnalysisCoverage;
  /** Model ids of the map and reduce passes. */
  models?: { map?: string; reduce?: string };
  promptVersion?: string;
  /**
   * Server-managed run record (ship 2026-10-06 audit r3 MED-2): the custom
   * guest fields — current NORMAL definitions at run time — whose values or
   * labels went into the model prompt; absent on analyses from before. Read
   * only to decide whether Velo may still read the model's free text
   * (`lib/goals/aiRestrictedFields.isAnalysisStaleForAi`).
   */
  promptFieldKeys?: string[] | null;
  /** The roster keys without a definition at run time whose values went into the prompt. */
  promptUndefinedKeys?: string[] | null;
  /** Set on the AI read only: the model saw a field Velo may not see now — re-run the analysis. */
  staleForAi?: boolean | null;
}

/**
 * Where the event stands in time, resolved BY THE BACKEND (the event shell
 * carries no dates). Read-only; never sent back.
 */
export interface GoalEventTiming {
  /** null for an event without a start date. */
  phase?: "UPCOMING" | "RUNNING" | "ENDED" | null;
  start?: string | null;
  end?: string | null;
  timeZone?: string | null;
  daysUntilStart?: number | null;
  daysSinceEnd?: number | null;
}

export interface EventGoalDoc {
  goalId?: string;
  eventId: string;
  goalText: string;
  eventTiming?: GoalEventTiming | null;
  kpis: EventGoalKpi[];
  /** Optional measurable cohort targets; old docs simply lack the field. */
  cohortTargets?: CohortTarget[];
  alertConfig: GoalAlertConfig;
  /** Server-managed; never sent on PUT. */
  analysis?: GoalAnalysis;
  /**
   * Gap titles the organizer dismissed (exact persisted `gap.title` strings).
   * Server-managed: written via the dismiss/restore endpoints, pruned by the
   * server when a new analysis completes; never sent on PUT.
   */
  dismissedGapTitles?: string[];
  createdAt?: string;
  updatedAt?: string;
  /** Server-managed: when the goal's SEMANTIC content (text, KPIs, cohort
   *  targets) last changed — every dismiss, claim and failure mark bumps
   *  `updatedAt` too, so the stale badge reads THIS. Absent on docs never
   *  edited since 2026-09-21. */
  goalEditedAt?: string;
}

/** One row of GET /event-goals/{eventId}/kpi-values. Join: `value.kpiId === kpi.id`. */
export interface KpiValue {
  kpiId: string;
  metric: KpiMetric;
  /** Null/absent when the metric is not evaluable (e.g. no seating plan). */
  currentValue?: number | null;
  target: number;
  thresholdValue: number;
  thresholdDirection: ThresholdDirection;
  breached: boolean;
  deadline?: string;
  enabled?: boolean;
  dimension?: string;
  bucketValue?: string;
  /** Within a group only (W3) — the denominator below is then that group. */
  baseDimension?: string | null;
  baseValue?: string | null;
  /**
   * The day the target is due, resolved BY THE BACKEND: the KPI's own deadline,
   * else the event start. The cockpit must not re-derive it — the event shell
   * (EVENT_LAYOUT_EVENT_FIELDS) does not carry `start`.
   */
  effectiveDeadline?: string | null;
  deadlineSource?: "KPI" | "EVENT_START" | "OUTCOME" | null;
  /** true = an after-event success measure. */
  outcome?: boolean | null;
  /**
   * Outcome KPIs only: before the event is over, while the field is being
   * filled in, or due. Before "DUE" the value is a work in progress.
   */
  outcomePhase?: "AWAITING_EVENT" | "COLLECTING" | "DUE" | null;
  /** true = a guest-derived value (cohort share) was blanked for this caller. */
  restricted?: boolean | null;
  /**
   * The receipt of a rate (acceptance, check-in, cohort share): guests counted
   * and the base they are counted over — "33 of 80". Absent for counts.
   */
  numerator?: number | null;
  denominator?: number | null;
}

export type KpiValueMap = Record<string, KpiValue>;

export function toKpiValueMap(values: KpiValue[]): KpiValueMap {
  const map: KpiValueMap = {};
  for (const value of values) {
    if (value.kpiId) map[value.kpiId] = value;
  }
  return map;
}

/** One stored day of a KPI. `value` null = the metric was not evaluable that day. */
export interface KpiHistoryPoint {
  /** Calendar day in the EVENT time zone, `YYYY-MM-DD`. Sortable as a string;
   *  never parse it into a local `Date`. */
  day: string;
  /** Null OR ABSENT on a day without a value — the backend's JSON-B drops null
   *  members (seen in e2e GP-8). Always test with `== null`. */
  value?: number | null;
}

/** One row of GET /event-goals/{eventId}/kpi-history. Join: `row.kpiId === kpi.id`. */
export interface KpiHistorySeries {
  kpiId: string;
  metric: KpiMetric;
  dimension?: string | null;
  bucketValue?: string | null;
  restricted?: boolean;
  points: KpiHistoryPoint[];
}

export type KpiHistoryMap = Record<string, KpiHistoryPoint[]>;

export function toKpiHistoryMap(series: KpiHistorySeries[]): KpiHistoryMap {
  const map: KpiHistoryMap = {};
  for (const row of series) {
    if (row.kpiId) map[row.kpiId] = row.points ?? [];
  }
  return map;
}

/**
 * Where the live KPI values are. "No value" has three different causes and the
 * page must not give the same answer to all of them: still loading and failed
 * to load both used to render as "No data yet — no emails sent yet", a false
 * statement about the event (audit 2026-09-18).
 */
export type KpiValuesState = "ready" | "loading" | "error";

/**
 * A KPI-list edit handed to the goals page: the new list, or — for anything
 * built FROM the list (add, replace, remove) — a function of it, which the page
 * resolves inside its serialized commit against the freshest list.
 */
export type KpisPatch = EventGoalKpi[] | ((current: EventGoalKpi[]) => EventGoalKpi[]);

/** The same shape for cohort targets — a merge must start from the list the
 *  previous commit left behind, not from the render that queued it. */
export type CohortTargetsPatch = CohortTarget[] | ((current: CohortTarget[]) => CohortTarget[]);
